import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Type,
  Image as ImageIcon,
  Sticker,
  Trash2,
  Save,
  X,
  Plus,
  ArrowLeft,
  BookOpen,
  Sparkles,
  Briefcase,
  Heart,
  CheckCircle,
  Move as MoveIcon,
  Download,
  Loader2,
  Palette,
  Zap,
  Eye,
  EyeOff,
  Undo,
  Redo,
  Share2,
  LayoutTemplate,
  Smartphone,
  Square,
  FileText,
  Maximize,
  Copy,
  Wand2,
  Grid3X3,
  Plane,
  Coins,
} from "lucide-react";

// --- Assets & Constants ---

const BACKGROUNDS = {
  default: {
    name: "Clean Grid",
    css: "radial-gradient(#cbd5e1 1px, transparent 1px)",
    color: "#f8fafc",
    size: "24px 24px",
  },
  paper: {
    name: "Warm Paper",
    css: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")',
    color: "#fdfbf7",
    size: "auto",
  }, // Fallback to color if url fails
  sky: {
    name: "Serene Sky",
    css: "linear-gradient(to top, #e6e9f0 0%, #eef1f5 100%)",
    color: "#eef1f5",
    size: "auto",
  },
  mint: {
    name: "Fresh Mint",
    css: "linear-gradient(120deg, #e0c3fc 0%, #8ec5fc 100%)",
    color: "#f0fdf4",
    size: "auto",
  },
  sunset: {
    name: "Soft Sunset",
    css: "linear-gradient(to top, #fff1eb 0%, #ace0f9 100%)",
    color: "#fff1eb",
    size: "auto",
  },
  dark: {
    name: "Midnight",
    css: "radial-gradient(#475569 1px, transparent 1px)",
    color: "#1e293b",
    size: "24px 24px",
  }, // Dark mode style
};

const BOARD_SIZES = {
  fullscreen: {
    name: "Full Screen",
    width: "100%",
    height: "100%",
    icon: <Maximize size={18} />,
  },
  mobile: {
    name: "Mobile Wallpaper",
    width: "375px",
    height: "812px",
    icon: <Smartphone size={18} />,
  },
  square: {
    name: "Instagram Square",
    width: "600px",
    height: "600px",
    icon: <Square size={18} />,
  },
  a4: {
    name: "A4 / Print",
    width: "595px",
    height: "842px",
    icon: <FileText size={18} />,
  },
};

const STICKER_PACKS = {
  essentials: {
    label: "Essentials",
    items: ["🎯", "⚡", "⭐", "🔥", "💡", "🚩", "✅", "📌"],
  },
  lifestyle: {
    label: "Life & Home",
    items: ["🏡", "❤️", "🧘", "🐶", "☕", "🥗", "📚", "🪴"],
  },
  wealth: {
    label: "Abundance",
    items: ["💸", "💻", "🎓", "🚀", "🏆", "💼", "📈", "🤝"],
  },
  travel: {
    label: "Adventure",
    items: ["✈️", "🌍", "🏖️", "🗺️", "📸", "🎒", "🏔️", "⛺"],
  },
};

const FILTERS = [
  { name: "Normal", value: "none" },
  { name: "B&W", value: "grayscale(100%) contrast(110%)" },
  { name: "Sepia", value: "sepia(80%) contrast(90%)" },
  {
    name: "Vintage",
    value: "sepia(40%) contrast(85%) brightness(110%) saturate(80%)",
  },
  { name: "Pop", value: "saturate(150%) contrast(110%)" },
];

const QUOTES = [
  "Dream big, start small.",
  "One day or day one?",
  "Focus on the step, not the stairs.",
  "Your potential is endless.",
  "Make it happen.",
  "Consistency > Intensity",
  "The future depends on what you do today.",
  "Believe you can.",
];

const VisionBoardApp = () => {
  // --- State ---
  const [view, setView] = useState("home");
  const [items, setItems] = useState([]);
  const [background, setBackground] = useState("default");
  const [boardSize, setBoardSize] = useState("fullscreen");
  const [timerActive, setTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [selectedId, setSelectedId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // UI States
  const [showStickerMenu, setShowStickerMenu] = useState(false);
  const [showBgMenu, setShowBgMenu] = useState(false);
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [zenMode, setZenMode] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  // Undo/Redo
  const [history, setHistory] = useState([[]]);
  const [historyStep, setHistoryStep] = useState(0);

  // Refs
  const boardRef = useRef(null);
  const fileInputRef = useRef(null);

  // --- Init ---
  useEffect(() => {
    // Load external scripts if needed
    const loadScript = (src) => {
      if (!document.querySelector(`script[src="${src}"]`)) {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        document.body.appendChild(script);
      }
    };

    // 1. Tailwind CSS (Essential for styling)
    loadScript("https://cdn.tailwindcss.com");

    // 2. Html2Canvas (For screenshots/saving)
    loadScript(
      "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"
    );

    // 3. Canvas Confetti (For celebrations)
    loadScript(
      "https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js"
    );
  }, []);

  // --- Timer ---
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && timerActive) {
      setTimerActive(false);
      setShowTimeUpModal(true);
      triggerConfetti();
    } else if (timeLeft === 0 && !showTimeUpModal) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft, showTimeUpModal]);

  // --- Load/Save ---
  useEffect(() => {
    const savedItems = localStorage.getItem("vision-board-data");
    const savedBg = localStorage.getItem("vision-board-bg");
    const savedSize = localStorage.getItem("vision-board-size");

    if (savedItems) {
      try {
        const parsed = JSON.parse(savedItems);
        setItems(parsed);
        setHistory([parsed]);
        setHistoryStep(0);
      } catch (e) {
        console.error(e);
      }
    }
    if (savedBg && BACKGROUNDS[savedBg]) setBackground(savedBg);
    if (savedSize && BOARD_SIZES[savedSize]) setBoardSize(savedSize);
  }, []);

  useEffect(() => {
    if (view === "workspace") {
      localStorage.setItem("vision-board-data", JSON.stringify(items));
      localStorage.setItem("vision-board-bg", background);
      localStorage.setItem("vision-board-size", boardSize);
    }
  }, [items, view, background, boardSize]);

  // --- Undo/Redo Logic ---
  const updateItemsWithHistory = (newItems, addToHistory = true) => {
    setItems(newItems);
    if (addToHistory) {
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(newItems);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    }
  };

  const handleUndo = () => {
    if (historyStep > 0) {
      const prevStep = historyStep - 1;
      setItems(history[prevStep]);
      setHistoryStep(prevStep);
      setSelectedId(null);
    }
  };

  const handleRedo = () => {
    if (historyStep < history.length - 1) {
      const nextStep = historyStep + 1;
      setItems(history[nextStep]);
      setHistoryStep(nextStep);
      setSelectedId(null);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        if (
          document.activeElement.tagName !== "TEXTAREA" &&
          document.activeElement.tagName !== "INPUT"
        ) {
          deleteItem(selectedId);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [historyStep, history, selectedId]);

  // --- Actions ---
  const triggerConfetti = () => {
    if (window.confetti)
      window.confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
  };

  const handleSave = () => {
    localStorage.setItem("vision-board-data", JSON.stringify(items));
    localStorage.setItem("vision-board-bg", background);
    localStorage.setItem("vision-board-size", boardSize);
    setShowSaveNotification(true);
    triggerConfetti();
    setTimeout(() => setShowSaveNotification(false), 2000);
  };

  const handleShare = async () => {
    if (!window.html2canvas || !boardRef.current) return;
    setIsDownloading(true);
    setSelectedId(null);
    setZenMode(true);
    setShowGrid(false);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      const canvas = await window.html2canvas(boardRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: null,
      });

      canvas.toBlob(async (blob) => {
        if (
          navigator.share &&
          blob &&
          navigator.canShare &&
          navigator.canShare({
            files: [new File([blob], "share.png", { type: "image/png" })],
          })
        ) {
          try {
            await navigator.share({
              title: "My 10-Minute Vision Board",
              text: "Created with Just 10 Minutes with Pen & Paper",
              files: [new File([blob], "board.png", { type: "image/png" })],
            });
            triggerConfetti();
          } catch (e) {
            console.log("Share cancelled");
          }
        } else {
          const link = document.createElement("a");
          link.download = `vision-board-${Date.now()}.png`;
          link.href = URL.createObjectURL(blob);
          link.click();
          triggerConfetti();
        }
        setIsDownloading(false);
        setZenMode(false);
      }, "image/png");
    } catch (err) {
      console.error(err);
      setIsDownloading(false);
      setZenMode(false);
      alert("Error sharing/saving.");
    }
  };

  // --- Templates ---
  const loadTemplate = (type) => {
    let newItems = [];
    const centerX = window.innerWidth / 2 - 100;
    const centerY = window.innerHeight / 2 - 100;

    if (type === "blank") newItems = [];
    else if (type === "reset") {
      newItems = [
        {
          id: 1,
          type: "text",
          content: "My 10-Minute Goal:\nWrite 100 words today.",
          x: centerX,
          y: centerY,
          width: 250,
          height: 120,
          color: "#fff9c4",
          rotation: -2,
          zIndex: 1,
        },
        {
          id: 2,
          type: "sticker",
          content: "⏳",
          x: centerX - 120,
          y: centerY - 50,
          rotation: -10,
          zIndex: 2,
        },
      ];
    } else if (type === "career") {
      newItems = [
        {
          id: 1,
          type: "text",
          content: "DREAM JOB\nManager by 2025",
          x: centerX,
          y: centerY - 50,
          width: 260,
          height: 100,
          color: "#bbdefb",
          rotation: 0,
          zIndex: 1,
        },
        {
          id: 2,
          type: "sticker",
          content: "🚀",
          x: centerX + 200,
          y: centerY - 80,
          rotation: 15,
          zIndex: 2,
        },
        {
          id: 3,
          type: "sticker",
          content: "💼",
          x: centerX - 100,
          y: centerY + 80,
          rotation: -5,
          zIndex: 2,
        },
      ];
    } else if (type === "wealth") {
      setBackground("mint");
      newItems = [
        {
          id: 1,
          type: "text",
          content: "FINANCIAL FREEDOM\nSave $5k this year",
          x: centerX,
          y: centerY,
          width: 260,
          height: 120,
          color: "#dcedc8",
          rotation: 0,
          zIndex: 1,
        },
        {
          id: 2,
          type: "sticker",
          content: "💸",
          x: centerX + 180,
          y: centerY - 80,
          rotation: 15,
          zIndex: 2,
        },
        {
          id: 3,
          type: "sticker",
          content: "📈",
          x: centerX - 100,
          y: centerY + 50,
          rotation: -10,
          zIndex: 2,
        },
      ];
    } else if (type === "travel") {
      setBackground("sky");
      newItems = [
        {
          id: 1,
          type: "text",
          content: "MY DREAM TRIP\nJapan 2026",
          x: centerX,
          y: centerY - 50,
          width: 240,
          height: 100,
          color: "#e0f7fa",
          rotation: -1,
          zIndex: 1,
        },
        {
          id: 2,
          type: "sticker",
          content: "✈️",
          x: centerX - 120,
          y: centerY - 80,
          rotation: -15,
          zIndex: 2,
        },
        {
          id: 3,
          type: "sticker",
          content: "🌏",
          x: centerX + 150,
          y: centerY + 50,
          rotation: 10,
          zIndex: 2,
        },
      ];
    }

    const stampedItems = newItems.map((item, idx) => ({
      ...item,
      id: Date.now() + idx,
    }));
    updateItemsWithHistory(stampedItems);
    setView("workspace");
    setTimerActive(true);
    setTimeLeft(600);
  };

  // --- Item Helpers ---
  const getBoardCenter = () => {
    if (boardRef.current)
      return {
        x: boardRef.current.clientWidth / 2,
        y: boardRef.current.clientHeight / 2,
      };
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  };

  const addItem = (type, content, extra = {}) => {
    const { x, y } = getBoardCenter();
    const newItem = {
      id: Date.now(),
      type,
      content,
      x: x - (type === "sticker" ? 50 : 100),
      y: y - (type === "sticker" ? 50 : 100),
      width: type === "sticker" ? 100 : 200,
      height: type === "sticker" ? 100 : type === "text" ? 100 : "auto",
      rotation: Math.random() * 6 - 3,
      zIndex: items.length + 1,
      ...extra,
    };
    updateItemsWithHistory([...items, newItem]);
    setSelectedId(newItem.id);
  };

  const addImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) =>
        addItem("image", evt.target.result, { filter: "none" });
      reader.readAsDataURL(file);
    }
  };

  // --- Item Manipulation ---
  const updateItem = (id, updates, addToHistory = false) => {
    const newItems = items.map((i) => (i.id === id ? { ...i, ...updates } : i));
    setItems(newItems);
    if (addToHistory) updateItemsWithHistory(newItems);
  };

  const deleteItem = (id) => {
    updateItemsWithHistory(items.filter((i) => i.id !== id));
    setSelectedId(null);
  };

  const duplicateItem = (id) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    const newItem = {
      ...item,
      id: Date.now(),
      x: item.x + 20,
      y: item.y + 20,
      zIndex: items.length + 1,
    };
    updateItemsWithHistory([...items, newItem]);
    setSelectedId(newItem.id);
  };

  const cycleFilter = (id) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    const currentIndex = FILTERS.findIndex(
      (f) => f.value === (item.filter || "none")
    );
    const nextIndex = (currentIndex + 1) % FILTERS.length;
    updateItem(id, { filter: FILTERS[nextIndex].value }, true);
  };

  // --- Drag & Drop ---
  const handleStart = (e, item) => {
    e.stopPropagation();
    setSelectedId(item.id);
    setDraggedItem(item);
    const maxZ = Math.max(...items.map((i) => i.zIndex || 0), 0);
    setItems(
      items.map((i) => (i.id === item.id ? { ...i, zIndex: maxZ + 1 } : i))
    );

    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({ x: cx - rect.left, y: cy - rect.top });
  };

  const handleMove = (e) => {
    if (draggedItem && boardRef.current) {
      e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const rect = boardRef.current.getBoundingClientRect();
      updateItem(
        draggedItem.id,
        { x: cx - rect.left - offset.x, y: cy - rect.top - offset.y },
        false
      );
    }
  };

  const handleEnd = () => {
    if (draggedItem) {
      updateItemsWithHistory(items);
      setDraggedItem(null);
    }
  };

  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${s % 60 < 10 ? "0" : ""}${s % 60}`;

  // --- RENDER ---
  if (view === "home")
    return (
      <div className="flex flex-col h-screen w-full bg-slate-50 font-sans overflow-y-auto">
        <div className="bg-white border-b border-slate-200 py-12 px-6 text-center shadow-sm relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#4f46e5 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          ></div>
          <div className="relative z-10">
            <div className="inline-flex items-center justify-center p-5 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl mb-6 shadow-xl transform rotate-3">
              <BookOpen size={40} />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
              Just 10 Minutes{" "}
              <span className="text-indigo-600">Vision Board</span>
            </h1>
            <p className="text-slate-500 max-w-lg mx-auto text-lg">
              The official digital companion. Visualize your dreams and organize
              your chaos in just 10 minutes a day.
            </p>
          </div>
        </div>

        <div className="flex-1 max-w-6xl mx-auto w-full p-6 md:p-12 pb-32">
          <h2 className="text-xl font-bold text-slate-700 mb-6 flex items-center gap-2">
            <Sparkles className="text-amber-500" size={20} /> Start with a
            Template
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <TemplateCard
              title="Blank Canvas"
              icon={<Plus size={32} />}
              color="slate"
              desc="Start from scratch"
              onClick={() => loadTemplate("blank")}
            />
            <TemplateCard
              title="The 10-Minute Reset"
              icon={<RotateCcw size={24} />}
              color="blue"
              isPopular
              desc="Focus on one immediate goal."
              onClick={() => loadTemplate("reset")}
            />
            <TemplateCard
              title="Career Clarity"
              icon={<Briefcase size={24} />}
              color="purple"
              desc="Visualize your promotion."
              onClick={() => loadTemplate("career")}
            />
            <TemplateCard
              title="Financial Freedom"
              icon={<Coins size={24} />}
              color="green"
              desc="Savings and abundance."
              onClick={() => loadTemplate("wealth")}
            />
            <TemplateCard
              title="Dream Travel"
              icon={<Plane size={24} />}
              color="cyan"
              desc="Plan your next adventure."
              onClick={() => loadTemplate("travel")}
            />
          </div>
        </div>
      </div>
    );

  return (
    <div
      className="flex flex-col h-screen w-full bg-slate-50 font-sans text-slate-800 overflow-hidden"
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
    >
      {/* Notifications */}
      {showSaveNotification && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-[80] bg-slate-800 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 animate-bounce">
          <Sparkles size={20} className="text-yellow-400" />{" "}
          <span className="font-medium">Saved! Keep Going!</span>
        </div>
      )}

      {/* Completion Modal */}
      {showTimeUpModal && (
        <div className="absolute inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center relative overflow-hidden animate-in slide-in-from-bottom-5">
            {/* Decorative background blob */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>

            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-600">
              <Sparkles size={32} />
            </div>

            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Time's Up! 🎉
            </h2>
            <p className="text-slate-500 mb-6">
              You've invested 10 focused minutes in your future. Great work!
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowTimeUpModal(false);
                  setTimerActive(true);
                  setTimeLeft(300);
                }}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={18} /> Add 5 Minutes
              </button>
              <button
                onClick={() => {
                  setShowTimeUpModal(false);
                  handleSave();
                }}
                className="w-full py-3 px-4 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
              >
                Save & Finish
              </button>
              <button
                onClick={() => setShowTimeUpModal(false)}
                className="w-full py-2 text-slate-400 text-sm hover:text-slate-600"
              >
                Just keep editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      {!zenMode && (
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 shadow-sm z-50 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView("home")}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 hidden sm:flex">
              <button
                onClick={handleUndo}
                disabled={historyStep <= 0}
                className={`p-1.5 rounded-md ${
                  historyStep <= 0
                    ? "text-slate-300"
                    : "hover:bg-white text-slate-600"
                }`}
              >
                <Undo size={16} />
              </button>
              <div className="w-px h-4 bg-slate-200"></div>
              <button
                onClick={handleRedo}
                disabled={historyStep >= history.length - 1}
                className={`p-1.5 rounded-md ${
                  historyStep >= history.length - 1
                    ? "text-slate-300"
                    : "hover:bg-white text-slate-600"
                }`}
              >
                <Redo size={16} />
              </button>
            </div>
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`p-2 rounded-lg transition-colors ${
                showGrid
                  ? "bg-indigo-50 text-indigo-600"
                  : "text-slate-400 hover:text-slate-600"
              }`}
              title="Toggle Grid"
            >
              <Grid3X3 size={20} />
            </button>
          </div>

          <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1 border border-slate-200">
            <div className="px-3 font-mono font-bold text-blue-600 w-16 text-center">
              {formatTime(timeLeft)}
            </div>
            <button
              onClick={() => setTimerActive(!timerActive)}
              className="p-1.5 bg-white rounded-full shadow-sm hover:text-blue-600 transition-colors"
            >
              {timerActive ? <Pause size={14} /> : <Play size={14} />}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setZenMode(true)}
              className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg hidden sm:block"
            >
              <EyeOff size={20} />
            </button>
            <button
              onClick={handleShare}
              disabled={isDownloading}
              className="flex items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl"
            >
              {isDownloading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Share2 size={16} />
              )}{" "}
              <span className="hidden sm:inline">Share</span>
            </button>
          </div>
        </header>
      )}

      {/* Workspace Area - Adjusted for variable board sizes */}
      <div
        className={`flex-1 flex flex-col md:flex-row relative overflow-hidden ${
          boardSize !== "fullscreen" ? "bg-slate-200/50" : ""
        }`}
      >
        {/* Canvas Container */}
        <div
          className={`flex-1 relative overflow-auto touch-none order-1 md:order-2 transition-all duration-300 ${
            boardSize !== "fullscreen"
              ? "flex items-center justify-center p-8"
              : ""
          }`}
          onClick={() => setSelectedId(null)}
        >
          {/* The Actual Board */}
          <div
            ref={boardRef}
            className={`relative transition-all duration-500 bg-white ${
              boardSize !== "fullscreen"
                ? "shadow-2xl ring-1 ring-black/5"
                : "w-full h-full"
            }`}
            style={{
              width: BOARD_SIZES[boardSize].width,
              height: BOARD_SIZES[boardSize].height,
              backgroundColor: BACKGROUNDS[background].color,
              backgroundImage: BACKGROUNDS[background].css,
              backgroundSize: BACKGROUNDS[background].size,
            }}
          >
            {/* Alignment Grid Overlay */}
            {showGrid && (
              <div
                className="absolute inset-0 pointer-events-none z-[60]"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, rgba(99, 102, 241, 0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(99, 102, 241, 0.1) 1px, transparent 1px)",
                  backgroundSize: "50px 50px",
                }}
              ></div>
            )}

            {zenMode && (
              <button
                onClick={() => setZenMode(false)}
                className="absolute top-4 right-4 z-[70] bg-white/80 backdrop-blur p-2 rounded-full shadow-lg text-slate-600 hover:text-indigo-600"
              >
                <Eye size={24} />
              </button>
            )}

            {items.map((item) => (
              <div
                key={item.id}
                style={{
                  position: "absolute",
                  left: item.x,
                  top: item.y,
                  zIndex: item.zIndex,
                  transform: `rotate(${item.rotation}deg)`,
                  cursor: "grab",
                }}
                onMouseDown={(e) => handleStart(e, item)}
                onTouchStart={(e) => handleStart(e, item)}
                className={`group ${selectedId === item.id ? "z-50" : ""}`}
              >
                {/* Content Types */}
                {item.type === "text" && (
                  <div
                    className={`p-4 shadow-md rounded-sm min-w-[150px] relative font-handwriting text-xl transition-shadow duration-200 ${
                      selectedId === item.id
                        ? "shadow-2xl ring-2 ring-indigo-400"
                        : "hover:shadow-lg"
                    }`}
                    style={{ backgroundColor: item.color }}
                  >
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-16 h-4 bg-yellow-200/40 blur-[2px] rounded-full"></div>
                    <textarea
                      value={item.content}
                      onChange={(e) =>
                        updateItem(item.id, { content: e.target.value }, true)
                      }
                      className="w-full h-full bg-transparent resize-none outline-none text-slate-800 placeholder-slate-500/50 cursor-text min-h-[80px]"
                      placeholder="Type..."
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                {item.type === "image" && (
                  <div
                    className={`relative p-2 bg-white shadow-md transition-shadow ${
                      selectedId === item.id
                        ? "shadow-2xl ring-2 ring-indigo-400"
                        : ""
                    }`}
                  >
                    <img
                      src={item.content}
                      alt="Vision"
                      className="pointer-events-none max-w-[200px] sm:max-w-[300px] block transition-all duration-300"
                      style={{ filter: item.filter || "none" }}
                    />
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-red-400 border border-red-600 shadow-sm z-10"></div>
                  </div>
                )}
                {item.type === "sticker" && (
                  <div
                    className={`text-6xl sm:text-7xl select-none filter drop-shadow-md transition-transform p-2 ${
                      selectedId === item.id ? "scale-110 drop-shadow-xl" : ""
                    }`}
                  >
                    {item.content}
                  </div>
                )}

                {/* Floating Controls */}
                {selectedId === item.id && !zenMode && (
                  <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-xl border border-slate-100 p-1 flex items-center gap-1 z-[70] animate-in fade-in zoom-in duration-200 whitespace-nowrap">
                    <ControlBtn
                      icon={<RotateCcw size={14} />}
                      onClick={() =>
                        updateItem(
                          item.id,
                          { rotation: item.rotation - 15 },
                          true
                        )
                      }
                    />
                    <div className="w-px h-4 bg-slate-200"></div>
                    <ControlBtn
                      icon={<Copy size={14} />}
                      onClick={() => duplicateItem(item.id)}
                    />

                    {item.type === "image" && (
                      <>
                        <div className="w-px h-4 bg-slate-200"></div>
                        <ControlBtn
                          icon={<Wand2 size={14} />}
                          onClick={() => cycleFilter(item.id)}
                          highlight
                        />
                      </>
                    )}

                    <div className="w-px h-4 bg-slate-200"></div>
                    <div
                      className="p-2 text-indigo-600 cursor-move"
                      onMouseDown={(e) => handleStart(e, item)}
                      onTouchStart={(e) => handleStart(e, item)}
                    >
                      <MoveIcon size={16} />
                    </div>
                    <div className="w-px h-4 bg-slate-200"></div>
                    <ControlBtn
                      icon={<Trash2 size={14} />}
                      onClick={() => deleteItem(item.id)}
                      isDanger
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar Toolbar */}
        {!zenMode && (
          <div className="bg-white border-t md:border-t-0 md:border-r border-slate-200 flex flex-row md:flex-col items-center justify-around md:justify-start py-2 md:py-6 gap-1 md:gap-4 z-40 shadow-sm h-16 md:h-full w-full md:w-20 order-2 md:order-1 shrink-0 px-2 md:px-0">
            <ToolButton
              icon={<Type size={20} />}
              label="Text"
              onClick={() => addItem("text", "New Note...")}
            />
            <ToolButton
              icon={<ImageIcon size={20} />}
              label="Image"
              onClick={() => fileInputRef.current.click()}
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={addImage}
              accept="image/*"
              className="hidden"
            />

            <div className="relative">
              <ToolButton
                icon={<Sticker size={20} />}
                label="Sticker"
                onClick={() => {
                  setShowStickerMenu(!showStickerMenu);
                  setShowBgMenu(false);
                  setShowLayoutMenu(false);
                }}
                isActive={showStickerMenu}
              />
              {showStickerMenu && (
                <div className="absolute bottom-16 md:bottom-auto md:left-16 md:top-0 bg-white shadow-2xl border border-slate-100 p-4 rounded-2xl w-72 z-50 animate-in slide-in-from-bottom-5 h-[400px] overflow-y-auto">
                  <h3 className="font-bold text-slate-800 mb-3 text-sm">
                    Sticker Library
                  </h3>
                  {Object.entries(STICKER_PACKS).map(([key, pack]) => (
                    <div key={key} className="mb-4">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        {pack.label}
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        {pack.items.map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => {
                              addItem("sticker", emoji);
                              setShowStickerMenu(false);
                            }}
                            className="text-2xl hover:bg-indigo-50 p-2 rounded-xl transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="w-8 h-px bg-slate-200 hidden md:block my-2"></div>

            <div className="relative">
              <ToolButton
                icon={<Palette size={20} />}
                label="Theme"
                onClick={() => {
                  setShowBgMenu(!showBgMenu);
                  setShowStickerMenu(false);
                  setShowLayoutMenu(false);
                }}
                isActive={showBgMenu}
              />
              {showBgMenu && (
                <div className="absolute bottom-16 md:bottom-auto md:left-16 md:top-20 bg-white shadow-2xl border border-slate-100 p-3 rounded-2xl flex flex-col gap-2 w-48 z-50 animate-in slide-in-from-bottom-5">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 mb-1">
                    Backgrounds
                  </h3>
                  {Object.entries(BACKGROUNDS).map(([key, bg]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setBackground(key);
                        setShowBgMenu(false);
                      }}
                      className={`flex items-center gap-3 p-2 rounded-lg text-sm font-medium transition-colors ${
                        background === key
                          ? "bg-indigo-50 text-indigo-600"
                          : "hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      <div
                        className="w-6 h-6 rounded-full border border-slate-200 shadow-sm"
                        style={{
                          backgroundColor: bg.color,
                          backgroundImage: bg.css,
                        }}
                      ></div>
                      {bg.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <ToolButton
                icon={<LayoutTemplate size={20} />}
                label="Layout"
                onClick={() => {
                  setShowLayoutMenu(!showLayoutMenu);
                  setShowBgMenu(false);
                  setShowStickerMenu(false);
                }}
                isActive={showLayoutMenu}
              />
              {showLayoutMenu && (
                <div className="absolute bottom-16 right-0 md:right-auto md:bottom-auto md:left-16 md:top-36 bg-white shadow-2xl border border-slate-100 p-3 rounded-2xl flex flex-col gap-2 w-52 z-50 animate-in slide-in-from-bottom-5">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 mb-1">
                    Canvas Size
                  </h3>
                  {Object.entries(BOARD_SIZES).map(([key, size]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setBoardSize(key);
                        setShowLayoutMenu(false);
                      }}
                      className={`flex items-center gap-3 p-2 rounded-lg text-sm font-medium transition-colors ${
                        boardSize === key
                          ? "bg-indigo-50 text-indigo-600"
                          : "hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      <div className="text-slate-400">{size.icon}</div>
                      {size.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <ToolButton
              icon={<Zap size={20} />}
              label="Inspire"
              onClick={() =>
                addItem(
                  "text",
                  QUOTES[Math.floor(Math.random() * QUOTES.length)]
                )
              }
              highlight
            />

            <div className="md:hidden flex-1"></div>
            <button
              onClick={handleSave}
              className="md:hidden p-2 text-indigo-600 bg-indigo-50 rounded-xl"
            >
              <Save size={20} />
            </button>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&display=swap');
        .font-handwriting { font-family: 'Kalam', cursive; }
      `}</style>
    </div>
  );
};

// --- Subcomponents ---
const TemplateCard = ({ title, icon, color, desc, onClick, isPopular }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100 hover:border-blue-400",
    purple:
      "bg-purple-50 text-purple-600 border-purple-100 hover:border-purple-400",
    green: "bg-green-50 text-green-600 border-green-100 hover:border-green-400",
    slate: "bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-400",
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-100 hover:border-cyan-400",
  };
  return (
    <button
      onClick={onClick}
      className={`relative group p-6 rounded-2xl border-2 text-left transition-all hover:shadow-xl hover:-translate-y-1 ${
        colors[color] || colors.slate
      }`}
    >
      {isPopular && (
        <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-white/80 px-2 py-1 rounded-full text-blue-600 shadow-sm">
          Popular
        </span>
      )}
      <div className="mb-4 bg-white/80 w-12 h-12 rounded-xl flex items-center justify-center shadow-sm">
        {icon}
      </div>
      <h3 className="font-bold text-slate-800 text-lg">{title}</h3>
      <p className="text-sm opacity-80 mt-2">{desc}</p>
    </button>
  );
};

const ToolButton = ({ icon, label, onClick, isActive, highlight }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center gap-1 p-1 rounded-xl transition-all w-full ${
      isActive ? "text-indigo-600" : "text-slate-500 hover:text-slate-800"
    }`}
  >
    <div
      className={`p-2 md:p-3 rounded-xl transition-all ${
        isActive ? "bg-indigo-100" : "bg-slate-50 group-hover:bg-white"
      } ${highlight ? "text-yellow-600 bg-yellow-50 hover:bg-yellow-100" : ""}`}
    >
      {icon}
    </div>
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

const ControlBtn = ({ icon, onClick, isDanger, highlight }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    onTouchStart={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`p-2 rounded-lg transition-colors ${
      isDanger
        ? "text-red-400 hover:text-red-600 hover:bg-red-50"
        : highlight
        ? "text-purple-600 hover:bg-purple-50"
        : "text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
    }`}
  >
    {icon}
  </button>
);

export default VisionBoardApp;
